import React, { useState, memo } from 'react';
import { ArrowLeft, Star, MoreVertical, Plus, Edit2, Mic, AudioLines, ChevronDown, Lock, FileText, Maximize2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../components/ThemeToggle';
import { Header } from '../components/Header';
import { WorkflowPipeline } from '../components/discovery/WorkflowPipeline';
import { copy } from '../copy';
export function ProjectPage() {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && inputValue.trim()) {
      e.preventDefault();
      navigate('/chat');
    }
  };
  const threads = copy.caseDetail.threads;
  const files = copy.caseDetail.files;
  return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] font-sans selection:bg-slate-200 dark:selection:bg-slate-700 transition-colors duration-200" data-id="element-470">
      <Header title={copy.caseDetail.backLabel} minimal showBackArrow backTo="/cases" data-id="element-471" />

      <div className="mx-auto max-w-[1400px] px-6 py-4" data-id="element-472">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-473">
          {/* Main Content (Left Column) */}
          <div className="flex-1 space-y-8" data-id="element-474">
            {/* Header */}
            <div className="flex items-start justify-between" data-id="element-475">
              <div data-id="element-476">
                <h1 className="text-4xl font-semibold text-slate-900 dark:text-white mb-2" data-id="element-477">
                  {copy.caseDetail.title}
                </h1>
                <p className="text-slate-500 dark:text-[#a3a3a3]" data-id="element-478">
                  {copy.caseDetail.subtitle}
                </p>
              </div>
              <div className="flex items-center gap-4" data-id="element-479">
                <button className="p-2 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-colors" data-id="element-480">
                  <MoreVertical size={20} className="text-slate-500 dark:text-[#a3a3a3]" data-id="element-481" />
                </button>
                <button className="p-2 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-colors" data-id="element-482">
                  <Star size={20} className="text-slate-500 dark:text-[#a3a3a3]" data-id="element-483" />
                </button>
              </div>
            </div>

            {/* Prominent Composer */}
            <div className="relative group" data-id="element-484">
              <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-4 focus-within:border-slate-300 dark:focus-within:border-[#444] transition-all shadow-sm dark:shadow-2xl" data-id="element-485">
                <textarea value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder={copy.caseDetail.composerPlaceholder} className="w-full bg-transparent text-lg outline-none placeholder:text-slate-400 dark:placeholder:text-[#555] text-slate-900 dark:text-white resize-none min-h-[120px] pb-12" data-id="element-486" />

                <div className="absolute bottom-6 left-6 flex items-center gap-2" data-id="element-487">
                  <button className="p-2 hover:bg-slate-200 dark:hover:bg-white/5 rounded-lg text-slate-500 dark:text-[#a3a3a3] transition-colors" data-id="element-488">
                    <Plus size={20} data-id="element-489" />
                  </button>
                </div>

                <div className="absolute bottom-6 right-6 flex items-center gap-4" data-id="element-490">
                  <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-white/5 text-sm text-slate-500 dark:text-[#a3a3a3] transition-colors" data-id="element-491">
                    <span data-id="element-492">{copy.caseDetail.model}</span>
                    <span className="text-slate-400 dark:text-[#555]" data-id="element-493">
                      {copy.caseDetail.mode}
                    </span>
                    <ChevronDown size={14} data-id="element-494" />
                  </button>
                  <button className="p-2 hover:bg-slate-200 dark:hover:bg-white/5 rounded-lg text-slate-500 dark:text-[#a3a3a3] transition-colors" data-id="element-495">
                    <Mic size={20} data-id="element-496" />
                  </button>
                  <button className="p-2 hover:bg-slate-200 dark:hover:bg-white/5 rounded-lg text-slate-500 dark:text-[#a3a3a3] transition-colors" data-id="element-497">
                    <AudioLines size={20} data-id="element-498" />
                  </button>
                </div>
              </div>
            </div>

            {/* Thread List */}
            <div className="space-y-1" data-id="element-499">
              {threads.map((thread, i) => <button key={i} onClick={() => navigate('/chat')} className="w-full text-left p-4 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 transition-colors border-b border-slate-100 dark:border-[#262626] last:border-0 group" data-id="element-500">
                  <div className="font-medium text-slate-900 dark:text-[#e5e5e5] group-hover:text-slate-900 dark:group-hover:text-white mb-1" data-id="element-501">
                    {thread.title}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-[#737373]" data-id="element-502">
                    Last message {thread.time}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Sidebar (Right Column) */}
          <div className="w-full lg:w-[400px] space-y-6" data-id="element-503">
            {/* Discovery Workflow Pipeline */}
            <WorkflowPipeline data-id="element-504" />

            {/* Memory Section */}
            <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-5 space-y-4" data-id="element-505">
              <div className="flex items-center justify-between" data-id="element-506">
                <h3 className="font-medium text-slate-900 dark:text-white" data-id="element-507">
                  {copy.caseDetail.memoryTitle}
                </h3>
                <div className="flex items-center gap-2" data-id="element-508">
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-slate-200 dark:bg-[#2a2a2a] text-[10px] text-slate-600 dark:text-[#a3a3a3]" data-id="element-509">
                    <Lock size={10} data-id="element-510" />
                    {copy.caseDetail.privilegedLabel}
                  </div>
                  <button className="p-1 hover:bg-slate-200 dark:hover:bg-white/5 rounded text-slate-500 dark:text-[#a3a3a3]" data-id="element-511">
                    <Edit2 size={14} data-id="element-512" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-600 dark:text-[#a3a3a3] leading-relaxed" data-id="element-513">
                {copy.caseDetail.memoryBody}
              </p>
              <div className="text-[10px] text-slate-400 dark:text-[#555]" data-id="element-514">
                {copy.caseDetail.memoryLastUpdated}
              </div>
            </div>

            {/* Instructions Section */}
            <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-5 space-y-4" data-id="element-515">
              <div className="flex items-center justify-between" data-id="element-516">
                <h3 className="font-medium text-slate-900 dark:text-white" data-id="element-517">
                  {copy.caseDetail.instructionsTitle}
                </h3>
                <button className="p-1 hover:bg-slate-200 dark:hover:bg-white/5 rounded text-slate-500 dark:text-[#a3a3a3]" data-id="element-518">
                  <Plus size={16} data-id="element-519" />
                </button>
              </div>
              <p className="text-sm text-slate-500 dark:text-[#737373]" data-id="element-520">
                {copy.caseDetail.instructionsBody}
              </p>
            </div>

            {/* Files Section */}
            <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-5 space-y-4" data-id="element-521">
              <div className="flex items-center justify-between" data-id="element-522">
                <h3 className="font-medium text-slate-900 dark:text-white" data-id="element-523">
                  {copy.caseDetail.filesTitle}
                </h3>
                <div className="flex items-center gap-1" data-id="element-524">
                  <button onClick={() => navigate('/case/files')} className="p-1 hover:bg-slate-200 dark:hover:bg-white/5 rounded text-slate-500 dark:text-[#a3a3a3]" title="Maximize" data-id="element-525">
                    <Maximize2 size={16} data-id="element-526" />
                  </button>
                  <button className="p-1 hover:bg-slate-200 dark:hover:bg-white/5 rounded text-slate-500 dark:text-[#a3a3a3]" data-id="element-527">
                    <Plus size={16} data-id="element-528" />
                  </button>
                </div>
              </div>

              <div className="space-y-2" data-id="element-529">
                <div className="h-1 w-full bg-slate-200 dark:bg-[#333] rounded-full overflow-hidden" data-id="element-530">
                  <div className="h-full w-[4%] bg-blue-500 rounded-full" data-id="element-531" />
                </div>
                <div className="flex items-center justify-between text-[10px]" data-id="element-532">
                  <span className="text-slate-500 dark:text-[#a3a3a3]" data-id="element-533">
                    {copy.caseDetail.capacityLabel}
                  </span>
                  <span className="flex items-center gap-1 text-slate-500 dark:text-[#a3a3a3]" data-id="element-534">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500" data-id="element-535" />
                    {copy.caseDetail.indexingLabel}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 pt-2" data-id="element-536">
                {files.map((file, i) => <div key={i} className="p-3 rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] space-y-2 hover:border-slate-300 dark:hover:border-[#444] transition-colors cursor-pointer group" data-id="element-537">
                    <div className="text-xs font-medium text-slate-900 dark:text-[#e5e5e5] line-clamp-2 leading-snug group-hover:text-slate-900 dark:group-hover:text-white" data-id="element-538">
                      {file.name}
                    </div>
                    <div className="text-[10px] text-slate-500 dark:text-[#555]" data-id="element-539">
                      {file.lines}
                    </div>
                    <div className="inline-block px-1.5 py-0.5 rounded bg-slate-100 dark:bg-[#2a2a2a] text-[9px] text-slate-500 dark:text-[#737373] font-bold" data-id="element-540">
                      TEXT
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}